// Pure client-side relevance ranking for the search sidebar's "Pertinence"
// sort. Tahiti's `search` expression only knows exact-match (`=x`) and
// substring/contains matching — it has no notion of match quality, so once a
// contains-match result set comes back, this module ranks it: a title that
// starts with the query outranks one that merely contains it elsewhere. No
// fuzzy/typo tolerance — that would require a broader candidate set than
// Tahiti's contains search returns (see the grilled design decision).
import type { WordSummary } from '@/schemas/word';

/** How a word's title relates to the query, for ranking purposes. */
export type MatchClass = 'prefix' | 'contains' | 'none';

/** Classify a title against a query: starts-with, merely contains, or neither. */
export function classifyMatch(title: string, query: string): MatchClass {
  const needle = query.trim().toLowerCase();
  if (!needle) return 'none';
  const haystack = title.toLowerCase();
  if (haystack.startsWith(needle)) return 'prefix';
  if (haystack.includes(needle)) return 'contains';
  return 'none';
}

function byTitleAsc(a: WordSummary, b: WordSummary): number {
  return a.title.localeCompare(b.title, 'fr');
}

/**
 * Rank a contains-matched result set: prefix matches first, then plain
 * contains matches, each tier sorted alphabetically as a deterministic
 * secondary key.
 */
export function rankContainsResults(items: WordSummary[], query: string): WordSummary[] {
  const prefixTier: WordSummary[] = [];
  const containsTier: WordSummary[] = [];
  for (const item of items) {
    if (classifyMatch(item.title, query) === 'prefix') prefixTier.push(item);
    else containsTier.push(item);
  }
  prefixTier.sort(byTitleAsc);
  containsTier.sort(byTitleAsc);
  return [...prefixTier, ...containsTier];
}

/** Strip items whose id is in `ids` (used to drop items already surfaced by the exact tier). */
export function excludeIds(items: WordSummary[], ids: ReadonlySet<string>): WordSummary[] {
  return items.filter((item) => !ids.has(item.id));
}

/** Concatenate the exact-match tier (always first) with the ranked remainder. */
export function mergeRelevanceResults(opts: {
  exact: WordSummary[];
  containsRanked: WordSummary[];
}): WordSummary[] {
  const exactIds = new Set(opts.exact.map((item) => item.id));
  return [...opts.exact, ...excludeIds(opts.containsRanked, exactIds)];
}
